import React from "react";
import { utils, writeFile } from "xlsx";
import { DatePicker } from "antd";
import locale from "antd/es/date-picker/locale/es_ES";
import dayjs, { Dayjs } from "dayjs";
import { getPositionMovements } from "../../../services/partner.service.ts";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from "recharts";
import { Menu, MenuItem } from "@mui/material";

const { RangePicker } = DatePicker;

interface PositionMovementItem {
  id: number;
  partnerId: number;
  name: string;
  oldPosition: string;
  newPosition: string;
  oldBranch: string;
  newBranch: string;
  approvalDate: string;
  requestDate: string;
  reasonForPositionChange: string | null;
  requesterId: number | null;
  requesterName: string | null;
}

const MovimientosPuestoDetalle: React.FC = () => {
  const [data, setData] = React.useState<PositionMovementItem[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [dateRange, setDateRange] = React.useState<[Dayjs, Dayjs] | null>(() => {
    const end = dayjs();
    const start = end.subtract(30, "day");
    return [start, end];
  });
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [contextMenu, setContextMenu] = React.useState<{
    mouseX: number;
    mouseY: number;
    partnerId: number;
  } | null>(null);

  const chartData = React.useMemo(() => {
    const counts = data.reduce<Record<string, number>>((acc, item) => {
      const key = item.newPosition || "Sin puesto";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [data]);

  const fetchData = React.useCallback(
    async (start?: string, end?: string) => {
      try {
        setLoading(true);
        const response = await getPositionMovements(undefined, start, end);
        setData(response || []);
      } catch (error) {
        console.error("Error fetching position movements report:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  React.useEffect(() => {
    if (dateRange && dateRange[0] && dateRange[1]) {
      const startDate = dateRange[0].format("YYYY-MM-DD");
      const endDate = dateRange[1].format("YYYY-MM-DD");
      fetchData(startDate, endDate);
    }
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleDateChange = (dates: (Dayjs | null)[] | null) => {
    if (!dates || !dates[0] || !dates[1]) return;
    setDateRange(dates as [Dayjs, Dayjs]);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const startDate = dates[0].format("YYYY-MM-DD");
    const endDate = dates[1].format("YYYY-MM-DD");

    debounceRef.current = setTimeout(() => {
      fetchData(startDate, endDate);
    }, 500);
  };

  const exportToExcel = () => {
    const ws = utils.aoa_to_sheet([]);
    const headers = [
      "Fecha solicitud",
      "Fecha aprobación",
      "Colaborador",
      "Puesto anterior",
      "Puesto nuevo",
      "Sucursal anterior",
      "Sucursal nueva",
      "Solicitado por",
      "Motivo",
    ];
    utils.sheet_add_aoa(ws, [headers], { origin: "A1" });

    let rowIndex = 2;
    data.forEach((item) => {
      const row = [
        item.requestDate ? dayjs(item.requestDate).format("DD/MM/YYYY") : "",
        item.approvalDate ? dayjs(item.approvalDate).format("DD/MM/YYYY") : "",
        item.name,
        item.oldPosition,
        item.newPosition,
        item.oldBranch,
        item.newBranch,
        item.requesterName || "",
        item.reasonForPositionChange || "",
      ];
      utils.sheet_add_aoa(ws, [row], { origin: `A${rowIndex}` });
      rowIndex++;
    });

    ws["!cols"] = [
      { wch: 15 },
      { wch: 15 },
      { wch: 32 },
      { wch: 28 },
      { wch: 28 },
      { wch: 24 },
      { wch: 24 },
      { wch: 28 },
      { wch: 40 },
    ];

    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Movimientos de puesto");
    writeFile(
      wb,
      `Movimientos_Puesto_${dayjs().format("YYYY-MM-DD")}.xlsx`
    );
  };

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div>
          <h3 style={styles.title}>Movimiento de personal por puesto</h3>
          <p style={styles.subtitle}>
            Detalle de cambios de puesto con origen, destino y solicitante.
          </p>
        </div>
        <div style={styles.headerActions}>
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setPickerOpen(!pickerOpen)}
              style={styles.dateButton}
              title="Seleccionar rango de fechas"
            >
              <i
                className="fa-regular fa-calendar"
                style={{ fontSize: "14px" }}
              ></i>
              <span style={{ marginLeft: 6, fontSize: 12 }}>
                {dateRange
                  ? `${dateRange[0].format("DD/MM/YYYY")} - ${dateRange[1].format(
                      "DD/MM/YYYY"
                    )}`
                  : "Rango de fechas"}
              </span>
            </button>
            <div
              style={{
                position: "absolute",
                top: "100%",
                right: 0,
                zIndex: 1000,
                visibility: pickerOpen ? "visible" : "hidden",
                opacity: pickerOpen ? 1 : 0,
              }}
            >
              <RangePicker
                open={pickerOpen}
                onOpenChange={(open) => setPickerOpen(open)}
                format="DD [de] MMMM [de] YYYY"
                placeholder={["Fecha de inicio", "Fecha de fin"]}
                size="small"
                allowClear={false}
                value={dateRange}
                onChange={handleDateChange}
                locale={locale}
                style={{
                  width: "0px",
                  height: "0px",
                  padding: 0,
                  border: "none",
                  overflow: "hidden",
                  position: "absolute",
                }}
                renderExtraFooter={() => <div style={{ padding: 8 }} />}
              />
            </div>
          </div>
          <button
            onClick={exportToExcel}
            style={styles.exportButton}
            title="Descargar Excel"
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#16a34a";
              e.currentTarget.style.borderColor = "#16a34a";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#22c55e";
              e.currentTarget.style.borderColor = "#22c55e";
            }}
          >
            <i
              className="fa-solid fa-file-arrow-down"
              style={styles.exportIcon}
            ></i>
            <span style={styles.exportText}>Excel</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div style={styles.loadingBody}>
          <i
            className="fa-solid fa-spinner fa-spin"
            style={{ fontSize: "20px", color: "#6b7280" }}
          ></i>
        </div>
      ) : data.length === 0 ? (
        <div style={styles.emptyBody}>
          <i
            className="fa-solid fa-check-circle"
            style={{
              fontSize: "24px",
              color: "#22c55e",
              marginBottom: "8px",
            }}
          ></i>
          <p style={{ fontSize: 12, margin: 0 }}>
            No hay movimientos de puesto en el período seleccionado.
          </p>
        </div>
      ) : (
        <div style={styles.contentGrid}>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Fecha sol.</th>
                  <th style={styles.th}>Fecha apr.</th>
                  <th style={styles.th}>Colaborador</th>
                  <th style={styles.th}>De puesto</th>
                  <th style={styles.th}>A puesto</th>
                  <th style={styles.th}>De sucursal</th>
                  <th style={styles.th}>A sucursal</th>
                  <th style={styles.th}>Solicita</th>
                </tr>
              </thead>
              <tbody>
              {data.map((item, index) => (
                <tr
                  key={item.id}
                  style={{
                    ...styles.tr,
                    ...(index % 2 === 1 ? styles.trAlt : {}),
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setContextMenu(
                      contextMenu === null
                        ? {
                            mouseX: e.clientX + 2,
                            mouseY: e.clientY - 6,
                            partnerId: item.partnerId || item.id,
                          }
                        : null
                    );
                  }}
                >
                  <td style={styles.tdSmall}>
                    {item.requestDate
                      ? dayjs(item.requestDate).format("DD/MM/YYYY")
                      : "-"}
                  </td>
                  <td style={styles.tdSmall}>
                    {item.approvalDate
                      ? dayjs(item.approvalDate).format("DD/MM/YYYY")
                      : "-"}
                  </td>
                  <td style={styles.td}>{item.name}</td>
                  <td style={styles.td}>{item.oldPosition}</td>
                  <td style={styles.td}>{item.newPosition}</td>
                  <td style={styles.td}>{item.oldBranch}</td>
                  <td style={styles.td}>{item.newBranch}</td>
                  <td style={styles.td}>{item.requesterName || "-"}</td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>

          <div style={styles.chartWrapper}>
            <div style={styles.chartTitleRow}>
              <span style={styles.chartTitleLabel}>
                Movimiento de personal por puesto
              </span>
            </div>
            <div style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 10, right: 10, left: 0, bottom: 40 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={70}
                    tick={{ fontSize: 11, fill: "#6b7280" }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: "#6b7280" }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <RechartsTooltip
                    formatter={(value: any) => [`${value} movimientos`, "Total"]}
                    contentStyle={{
                      borderRadius: 8,
                      border: "1px solid #e5e7eb",
                      boxShadow: "0 4px 10px rgba(15, 23, 42, 0.08)",
                      fontSize: 11,
                    }}
                  />
                  <Bar
                    dataKey="value"
                    fill="#4b5563"
                    radius={[6, 6, 0, 0]}
                    barSize={26}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
      <Menu
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        PaperProps={{
          style: {
            backgroundColor: "#ffffff",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
            borderRadius: "8px",
          },
        }}
      >
        <MenuItem
          style={{
            display: "flex",
            alignItems: "center",
            padding: "12px 16px",
            fontSize: "16px",
          }}
          onClick={() => {
            if (contextMenu) {
              const url = `${window.location.origin}/colaboradores/detalles/${contextMenu.partnerId}`;
              window.open(url, "_blank", "noopener,noreferrer");
            }
            setContextMenu(null);
          }}
        >
          <i
            className="fa-regular fa-user"
            style={{ marginRight: "12px", fontSize: "18px" }}
          ></i>
          Perfil
        </MenuItem>
      </Menu>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  card: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.08)",
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    height: "100%",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    paddingBottom: 8,
    borderBottom: "1px solid #e5e7eb",
  },
  title: {
    margin: 0,
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
  },
  subtitle: {
    margin: "4px 0 0 0",
    fontSize: 11,
    color: "#6b7280",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  dateButton: {
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: 999,
    padding: "4px 10px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    color: "#374151",
    fontSize: 12,
  },
  exportButton: {
    background: "#22c55e",
    border: "1px solid #22c55e",
    borderRadius: 999,
    padding: "4px 10px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  exportIcon: {
    fontSize: 11,
    color: "#ffffff",
  },
  exportText: {
    fontSize: 11,
    fontWeight: 600,
    color: "#ffffff",
  },
  loadingBody: {
    padding: "24px 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyBody: {
    padding: "24px 0",
    textAlign: "center",
    color: "#6b7280",
  },
  tableWrapper: {
    marginTop: 4,
    maxHeight: 340,
    overflowY: "auto",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 11,
  },
  th: {
    position: "sticky",
    top: 0,
    backgroundColor: "#f9fafb",
    padding: "6px 8px",
    textAlign: "left",
    fontWeight: 600,
    color: "#6b7280",
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
  },
  tr: {
    borderBottom: "1px solid #e5e7eb",
    cursor: "pointer",
  },
  trAlt: {
    backgroundColor: "#f9fafb",
  },
  td: {
    padding: "6px 8px",
    color: "#111827",
    verticalAlign: "middle",
  },
  tdSmall: {
    padding: "6px 8px",
    color: "#4b5563",
    whiteSpace: "nowrap",
  },
  contentGrid: {
    marginTop: 4,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.6fr) minmax(0, 1fr)",
    gap: 12,
    alignItems: "stretch",
  },
  chartWrapper: {
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    backgroundColor: "#f9fafb",
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  chartTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  chartTitleLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#111827",
  },
};

export default MovimientosPuestoDetalle;


